import { BsCheckLg as CheckIcon } from 'react-icons/bs'
import styled from 'styled-components'

import {
  CarouselIcon,
  ReelIcon,
  VideoIcon,
  StoryIcon,
  TextIcon,
  UnknownPlatformLogo,
  PlaceholderPhone,
  RejectIcon,
} from 'Icons'
import { type PostType } from 'Types'

import { FlexAlignCenter } from './CommonStyleComponents'

export interface PostPreviewGridProps {
  account: {
    name: string
    handler_name: string
    avatar: string
  }
  posts: {
    id: string
    status: string | 'posted'
    media: {
      type: PostType
      src: string
    }
  }[]
  isHighlightFirstGridItem?: boolean
  isReadOnly: boolean
}

const Wrap = styled.div`
  @media (max-width: 767px) {
    margin-left: 53px;
    margin-top: 22px;
  }
`

const PlaceholderPhoneStyled = styled(PlaceholderPhone)`
  position: absolute;
  min-width: 400px;
  width: 400px;
  top: 0;
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: 295px;
  gap: 5px;
  margin-top: 16px;
  max-height: 387px;
  overflow-x: auto;
  margin-left: auto;
  margin-right: auto;
`

const Account = styled(FlexAlignCenter)`
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  width: auto;

  img {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    margin-right: 8px;
  }

  .handler {
    font-weight: normal;
    line-height: 10px;

    p {
      white-space: nowrap;
      width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`

export const IsPendingMarker = styled(UnknownPlatformLogo)`
  position: absolute;
  width: 25px;
  right: 0;
  bottom: 0;
  z-index: 2;
  border-radius: 0;
  border-top-left-radius: 5px;

  rect {
    fill: #fcd34d;
  }

  path {
    fill: #333;
  }
`

export const IsApprovedMarker = styled.div`
  position: absolute;
  width: 25px;
  right: 0;
  bottom: 0;
  height: 25px;
  z-index: 2;
  background: #10b981;
  border-top-left-radius: 5px;
  display: grid;
  place-content: center;

  svg {
    width: 14px;
    position: relative;
    left: 1px;
    top: 1px;

    path {
      fill: #ffffff;
    }
  }
`

const IsRejectedMarker = styled(IsApprovedMarker)`
  background: #fa4f38;

  svg {
    width: 21px;

    rect {
      fill: transparent;
    }

    path {
      stroke-width: 2.5;
    }
  }
`

export const MediaIconWrap = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 25px;
  padding: 5px;
`

const GridItem = styled.div<{
  $isPosted: boolean
  $isHighlightFirstGridItem?: boolean
  $isFirst: boolean
}>`
  position: relative;
  aspect-ratio: 4 / 5; /* Using aspect-ratio property for 4:5 ratio */

  &:first-child {
    border-top-left-radius: 10px;
    border: ${(props) =>
      props.$isHighlightFirstGridItem &&
      props.$isFirst &&
      `4px solid #4f31ff;`};

    > img {
      border-top-left-radius: 6px;
    }
  }

  &:nth-child(3) {
    border-top-right-radius: 10px;
    > img {
      border-top-right-radius: 10px;
    }
  }

  &:nth-child(7) {
    border-bottom-left-radius: 10px;
    > img {
      border-bottom-left-radius: 10px;
    }
  }

  &:nth-child(10) {
    border-bottom-left-radius: 10px;
    > img {
      border-bottom-left-radius: 10px;
    }
  }

  > img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }

  ${(props) =>
    props.$isPosted &&
    `
  opacity: 0.7;
  `}
`

export const renderPostTypeIcon = (type: PostType) => {
  switch (type) {
    case 'carousel':
      return <CarouselIcon />
    case 'singleImage':
      return null
    case 'video':
      return <VideoIcon />
    case 'reel':
      return <ReelIcon />
    case 'story':
      return <StoryIcon />
    case 'text':
      return <TextIcon data-testid="text_icon" />
  }
}

const PostPreviewGrid: React.FC<
  PostPreviewGridProps & React.HTMLAttributes<HTMLDivElement>
> = ({ account, posts, isHighlightFirstGridItem, className, isReadOnly }) => {
  return (
    <Wrap className={className}>
      <PlaceholderPhoneStyled className="phone-bg" />
      <div className="container" data-testid="preview-grid-container">
        <Account>
          <img src={account.avatar} alt={account.handler_name} />
          <div>
            <p>{account.name}</p>
            <p className="handler">{account.handler_name}</p>
          </div>
        </Account>
        <GridContainer className="grid-container">
          {posts.map((post, index) => (
            <GridItem
              key={post.id}
              $isPosted={post.status === 'posted'}
              $isFirst={index === 0}
              className={index === 0 ? 'first-grid-item' : ''}
              $isHighlightFirstGridItem={isHighlightFirstGridItem}
            >
              <img src={post.media.src} alt={post.media.type} />
              <MediaIconWrap>
                {renderPostTypeIcon(post.media.type)}
              </MediaIconWrap>
              {!isReadOnly && (
                <>
                  {post.status === 'pending' && <IsPendingMarker />}
                  {post.status === 'approved' && (
                    <IsApprovedMarker>
                      <CheckIcon />
                    </IsApprovedMarker>
                  )}
                  {post.status === 'requestChange' && (
                    <IsRejectedMarker>
                      <RejectIcon />
                    </IsRejectedMarker>
                  )}
                </>
              )}
            </GridItem>
          ))}
        </GridContainer>
      </div>
    </Wrap>
  )
}

export default PostPreviewGrid
